import { useEffect, useRef, useState } from 'react';

interface ScoreCircleProps {
  score: number;
  maxScore?: number;
  size?: number;
  color?: string;
}

/** 0 → target 까지 duration 동안 tween. mount 시 한 번만. */
const useTweenedValue = (target: number, duration = 900) => {
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    let rafId = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration]);

  return value;
};

/**
 * Figma 486:7791 종합 점수 원형 게이지.
 * - Figma angular gradient stops 을 CSS conic-gradient 로 재현 (red→yellow→green→blue 풀 360°)
 * - 진행도(ratio) 만큼 mask 로 노출
 * - 중앙 흰 원(drop shadow) + 점수 텍스트
 * - 끝점 흰 dot (soft shadow)
 */
const ScoreCircle = ({
  score,
  maxScore = 100,
  size = 130,
  color = '#10C01E',
}: ScoreCircleProps) => {
  const clamped = Math.max(0, Math.min(score, maxScore));
  // mount 시 0 → clamped 까지 tween (숫자 카운팅 + 게이지 차오르기 동시)
  const animatedScore = useTweenedValue(clamped);
  const ratio = maxScore > 0 ? animatedScore / maxScore : 0;
  const strokeWidth = 2;

  const angleDeg = ratio * 360 - 90;
  const angleRad = (angleDeg * Math.PI) / 180;
  const ringRadius = size / 2 - strokeWidth / 2;
  const dotX = size / 2 + Math.cos(angleRad) * ringRadius;
  const dotY = size / 2 + Math.sin(angleRad) * ringRadius;

  const progressPercent = ratio * 100;

  // Figma angular gradient stops (stops 그대로):
  //  0%    #FF9496 (light red)
  //  31.25%  #FFDE5A (yellow)
  //  61.058% #ADCD79 (green)
  //  100%   #6991FF (blue)
  const fullGradient =
    'conic-gradient(from 0deg, #FF9496 0%, #FFDE5A 31.25%, #ADCD79 61.058%, #6991FF 100%)';

  // 진행도만큼 노출하는 mask (progress % 까지 불투명, 이후 투명)
  const arcMask =
    ratio >= 1
      ? 'linear-gradient(#000, #000)'
      : `conic-gradient(from 0deg, #000 ${progressPercent}%, transparent ${progressPercent}%)`;

  const innerInset = strokeWidth + Math.round(size * 0.07);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* 회색 풀 원 (배경) */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{ background: '#F0F0F0' }}
      />
      {/* 그라디언트 풀 원 → 진행도만큼 mask */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background: fullGradient,
          WebkitMaskImage: arcMask,
          maskImage: arcMask,
        }}
      />
      {/* 중앙 흰 원 — strokeWidth 만큼만 제외해서 ring 만 노출 */}
      <div
        aria-hidden
        className="absolute rounded-full bg-white"
        style={{
          top: strokeWidth,
          left: strokeWidth,
          right: strokeWidth,
          bottom: strokeWidth,
        }}
      />
      {/* 내부 흰 원 + drop shadow + 점수 텍스트 */}
      <div
        className="absolute flex flex-col items-center justify-center rounded-full bg-white"
        style={{
          top: innerInset,
          left: innerInset,
          right: innerInset,
          bottom: innerInset,
          boxShadow: '0px 3px 24px 0px rgba(138, 140, 169, 0.25)',
        }}
      >
        <span className="Title3S18" style={{ color }}>
          {Math.round(animatedScore)}점
        </span>
        <span className="text-gr-500 Caption1M12">/ {maxScore}점</span>
      </div>
      {/* 끝점 흰 dot */}
      {ratio > 0 && ratio < 1 && (
        <div
          aria-hidden
          className="absolute"
          style={{
            width: 8,
            height: 8,
            left: dotX - 4,
            top: dotY - 4,
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 0px 4px 0px rgba(135, 140, 182, 0.5)',
          }}
        />
      )}
    </div>
  );
};

export default ScoreCircle;
